import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    Stack
} from '@mui/material';
import {
    Send as SendIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    ExpandMore as ExpandMoreIcon,
    PersonSearch as PersonSearchIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, PANEL_SHOP_QC_STORE, EMPLOYEE_STORE, TEMPS_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import panelShopQcService from '../../../services/panelShopQcService';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import useDictation from '../../../hooks/useDictation';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const PanelShopQcForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [temps, setTemps] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        project: '',
        jobCode: '',
        jobName: '',
        date: dayjs().format('YYYY-MM-DD'),
        builder: '',
        fabricator: '',
        serialNumber: '',
        notes: '',
        pictures: [],
        
        // Checklist items
        visualChecks: {
            printsLocated: '',
            partsMissing: '',
            customerResponsible: '',
            sendingFieldTech: '',
            qualitySticker: '',
            printsUpToDate: '',
            printsHighlighted: '',
            changesNotated: '',
            redlinesApproved: '',
            devicesMounted: '',
            backpanLabels: '',
            wireMarkersCorrect: '',
            doorFaceLabels: '',
            ulStickers: '',
            terminalMarkersCorrect: '',
            panduitCovers: '',
            wiresTrunked: '',
            panelCleaned: '',
            doorsGrounded: '',
            jumperBarsCut: '',
            ferrulesPresent: '',
            overallWorkmanship: ''
        },
        
        pointToPointChecks: {
            wireAwgCorrect: '',
            wireColorsCorrect: '',
            volt600: '',
            volt480: '',
            volt380: '',
            volt230: '',
            volt208: '',
            volt120: '',
            volt24: '',
            safetyCircuit: '',
            meterJumperBars: '',
            fusesInstalled: '',
            wiresTerminated: '',
            wiresTorqued: '',
            devicesTorqued: '',
            pulledTestApplied: '',
            panelWiredCorrectly: ''
        },
        
        powerChecks: {
            powerApplied: '',
            powerAppliedBy: '',
            voltageVerified: '',
            voltage600: '',
            voltage480: '',
            voltage230: '',
            voltage208: '',
            voltage120: '',
            componentsPowerUp: ''
        },
        
        finalChecks: {
            correctionCompleted: '',
            wireMarkersHeatShrunk: '',
            picturesTaken: '',
            literaturePackaged: ''
        },
        
        supervisorId: userId,
        supervisorName: userFullName,
        supervisorSignature: '',
        supervisorSignatureDate: dayjs().format('YYYY-MM-DD'),
        
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [builderModalOpen, setBuilderModalOpen] = useState(false);
    const [fabricatorModalOpen, setFabricatorModalOpen] = useState(false);
    const [powerAppliedByModalOpen, setPowerAppliedByModalOpen] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [cameraTarget, setCameraTarget] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Dictation for notes
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });
    const [dictationField, setDictationField] = useState(null);

    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            label: `${job.Job} - ${job.JobName}`
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            if (db.objectStoreNames.contains(JOB_STORE)) {
                const tx = db.transaction(JOB_STORE, 'readonly');
                const store = tx.objectStore(JOB_STORE);
                const jobsData = await store.getAll();
                setJobs(jobsData || []);
            }

                if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const store = tx.objectStore(EMPLOYEE_STORE);
                    const allEmployees = await store.getAll();
                    setEmployees(allEmployees || []);
                }

                if (db.objectStoreNames.contains(TEMPS_STORE)) {
                    const tx = db.transaction(TEMPS_STORE, 'readonly');
                    const store = tx.objectStore(TEMPS_STORE);
                    const allTemps = await store.getAll();
                    setTemps(allTemps || []);
                }

            if (uuid) {
                const existingForm = await panelShopQcService.getFromIndexedDB(uuid);
                if (existingForm) {
                    setForm(existingForm);
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }
        };
        loadData();
    }, [uuid]);


    const queueSave = (formData) => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(() => {
            saveDraft(formData);
        }, 1000);
    };

    const saveDraft = async (formData) => {
        try {
            await panelShopQcService.saveToIndexedDB(formData);
        } catch (error) {
            console.error('Error saving draft:', error);
        }
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const handleCheckChange = (section, field, value) => {
        const updated = {
            ...form,
            [section]: {
                ...form[section],
                [field]: value
            }
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleJobChange = (event, newValue) => {
        if (!newValue) {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                project: ''
            };
            setForm(updated);
            queueSave(updated);
            return;
        }

        const updated = {
            ...form,
            jobCode: newValue.Job || '',
            jobName: newValue.JobName || '',
            project: newValue.JobName || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;

        const dataUrl = sigPadRef.current.toDataURL();
        const updated = {
            ...form,
            supervisorSignature: dataUrl,
            supervisorSignatureDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const updated = {
            ...form,
            supervisorSignature: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSelectEmployee = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            supervisorId: person.id || '',
            supervisorName: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    const handleSelectBuilder = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            builder: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setBuilderModalOpen(false);
    };

    const handleSelectFabricator = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            fabricator: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setFabricatorModalOpen(false);
    };

    const handleSelectPowerAppliedBy = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            powerChecks: {
                ...form.powerChecks,
                powerAppliedBy: person.FullName || person.name || ''
            }
        };
        setForm(updated);
        queueSave(updated);
        setPowerAppliedByModalOpen(false);
    };

    // Handle dictation for notes
    const handleStartDictation = (field) => {
        setDictationField(field);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm[field] || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        [field]: newText
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        setDictationField(null);
        stopDictation();
    };

    // Handle photo capture
    const handleCapturePhoto = () => {
        setCameraTarget(true);
    };

    const handlePhotoCapture = (photoData) => {
        const pictures = [...(form.pictures || [])];
        pictures.push({
            id: generateUUID(),
            dataUrl: photoData.dataUrl,
            name: photoData.name,
            timestamp: photoData.timestamp,
            description: ''
        });
        handleFieldChange('pictures', pictures);
        setCameraTarget(null);
    };

    const handleRemovePhoto = (photoId) => {
        const pictures = form.pictures?.filter(p => p.id !== photoId) || [];
        handleFieldChange('pictures', pictures);
    };

    const handlePhotoDescriptionChange = (photoId, description) => {
        const pictures = form.pictures.map(p => 
            p.id === photoId ? { ...p, description } : p
        );
        handleFieldChange('pictures', pictures);
    };

    // Calculate progress for each section
    const getSectionProgress = (section) => {
        const sectionData = form[section];
        if (!sectionData || typeof sectionData !== 'object') return { completed: 0, total: 0 };
        
        const keys = Object.keys(sectionData);
        const total = keys.length;
        const completed = keys.filter(key => {
            const value = sectionData[key];
            return value && value !== '' && value !== null && value !== undefined;
        }).length;
        
        return { completed, total };
    };

    const getSectionColor = (section) => {
        const { completed, total } = getSectionProgress(section);
        if (completed === 0) return '#9e9e9e'; // Gray - not started
        if (completed === total) return '#4caf50'; // Green - completed
        return '#ff9800'; // Orange - in progress
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.jobCode) {
            newErrors.jobCode = 'Job is required';
        }

        if (!form.supervisorSignature) newErrors.supervisorSignature = 'Supervisor signature is required';
        if (!form.supervisorName) newErrors.supervisorName = 'Supervisor name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Once submitted, the form cannot be edited',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);
            const submitData = { ...form, status: STATUS_SUBMITTED };
            await panelShopQcService.submit(submitData);
            await panelShopQcService.deleteFromIndexedDB(form.uuid);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Panel Shop Q/C Checklist submitted successfully'
            });

            navigate('/electrical/forms/panel-shop-qc/list');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    const renderCheckItem = (section, field, label) => (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            p: 2,
            mb: 1,
            borderRadius: 2,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: '#1976d2',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
            }
        }}>
            <Typography variant="body2" sx={{ 
                flex: 1, 
                pr: 3, 
                fontSize: '0.9rem', 
                lineHeight: 1.5,
                color: '#333',
                fontWeight: 400
            }}>
                {label}
            </Typography>
            <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexShrink: 0,
                alignItems: 'center'
            }}>
                <Box 
                    onClick={() => !submitted && handleCheckChange(section, field, 'yes')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: submitted ? 'default' : 'pointer',
                        opacity: submitted ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        p: 0.5,
                        borderRadius: 1,
                        '&:hover': !submitted ? {
                            backgroundColor: 'rgba(76, 175, 80, 0.1)'
                        } : {}
                    }}
                >
                    <Box sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid',
                        borderColor: form[section][field] === 'yes' ? '#4caf50' : '#ccc',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: form[section][field] === 'yes' ? '#4caf50' : 'transparent',
                        transition: 'all 0.2s ease'
                    }}>
                        {form[section][field] === 'yes' && (
                            <Box sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'white',
                                borderRadius: '50%'
                            }} />
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ 
                        ml: 1, 
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: form[section][field] === 'yes' ? '#4caf50' : '#666'
                    }}>
                        YES
                    </Typography>
                </Box>
                
                <Box 
                    onClick={() => !submitted && handleCheckChange(section, field, 'no')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: submitted ? 'default' : 'pointer',
                        opacity: submitted ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        p: 0.5,
                        borderRadius: 1,
                        '&:hover': !submitted ? {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)'
                        } : {}
                    }}
                >
                    <Box sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid',
                        borderColor: form[section][field] === 'no' ? '#f44336' : '#ccc',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: form[section][field] === 'no' ? '#f44336' : 'transparent',
                        transition: 'all 0.2s ease'
                    }}>
                        {form[section][field] === 'no' && (
                            <Box sx={{
                                width: 8,
                                height: 8,
                                backgroundColor: 'white',
                                borderRadius: '50%'
                            }} />
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ 
                        ml: 1, 
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: form[section][field] === 'no' ? '#f44336' : '#666'
                    }}>
                        NO
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/electrical/forms/panel-shop-qc/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>PANEL SHOP Q/C CHECKLIST</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {savingDraft && (
                            <Chip
                                label="Saving..."
                                size="small"
                                sx={{
                                    backgroundColor: '#2196f3',
                                    color: 'white'
                                }}
                            />
                        )}
                        <Chip
                            label={submitted ? 'Submitted' : 'Draft'}
                            size="small"
                            sx={{
                                backgroundColor: submitted ? '#4caf50' : '#9e9e9e',
                                color: 'white'
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Job Information */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Autocomplete
                                        options={sortedJobs}
                                        getOptionLabel={(option) => option.label || ''}
                                        value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                        onChange={handleJobChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="JOB # *"
                                                error={!!errors.jobCode}
                                                helperText={errors.jobCode}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Job Name"
                                        value={form.project}
                                        onChange={(e) => handleFieldChange('project', e.target.value)}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="DATE"
                                        type="date"
                                        value={form.date}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                        onChange={(e) => handleFieldChange('date', e.target.value)}
                                        disabled={submitted}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ style: { cursor: submitted ? 'default' : 'pointer' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="BUILDER"
                                        value={form.builder}
                                        onClick={() => {
                                            if (!submitted) setBuilderModalOpen(true);
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setBuilderModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="FABRICATOR"
                                        value={form.fabricator}
                                        onClick={() => {
                                            if (!submitted) setFabricatorModalOpen(true);
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setFabricatorModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="SERIAL #"
                                        value={form.serialNumber}
                                        onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
                                        disabled={submitted}
                                        inputProps={{ inputMode: 'numeric' }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="NOTES"
                                        value={form.notes}
                                        onChange={(e) => handleFieldChange('notes', e.target.value)}
                                        disabled={submitted}
                                        multiline
                                        rows={2}
                                        InputProps={{
                                            endAdornment: !submitted && speechSupported && (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            dictationField === 'notes'
                                                                ? handleStopDictation()
                                                                : handleStartDictation('notes')
                                                        }
                                                        color={dictationField === 'notes' ? 'error' : 'default'}
                                                    >
                                                        {dictationField === 'notes' ? <MicOffIcon /> : <MicIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* VISUAL Checklist */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' },
                                backgroundColor: getSectionColor('visualChecks') + '15'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
                                    Visual Checklist
                                </Typography>
                                <Chip
                                    label={`${getSectionProgress('visualChecks').completed} / ${getSectionProgress('visualChecks').total}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: getSectionColor('visualChecks'),
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 24
                                    }}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {renderCheckItem('visualChecks', 'printsLocated', '1. ARE PRINTS LOCATED IN THE PANEL OR PRINT POCKET?')}
                            {renderCheckItem('visualChecks', 'partsMissing', '2. ARE PARTS MISSING?')}
                            {renderCheckItem('visualChecks', 'customerResponsible', '2A. IS CUSTOMER RESPONSIBLE FOR INSTALLING MISSING PART(S)?')}
                            {renderCheckItem('visualChecks', 'sendingFieldTech', '2B. ARE WE SENDING A FIELD TECH TO INSTALL MISSING PART(S)?')}
                            {renderCheckItem('visualChecks', 'qualitySticker', '3. IS STICKER FOR QUALITY CHECK APPLIED IN TOP LEFT CORNER?')}
                            {renderCheckItem('visualChecks', 'printsUpToDate', '4. ARE THE PRINTS THE MOST UP TO DATE SET?')}
                            {renderCheckItem('visualChecks', 'printsHighlighted', '4A. HAVE PRINTS BEEN HIGHLIGHTED BY PANEL TECH?')}
                            {renderCheckItem('visualChecks', 'changesNotated', '5. HAVE ALL CHANGES BEEN NOTATED AND INTIALIZED BY MANAGER?')}
                            {renderCheckItem('visualChecks', 'redlinesApproved', '5B. HAVE REDLINES BEEN APPROVED BY CUSTOMER WITH EMAIL ATTACHED?')}
                            {renderCheckItem('visualChecks', 'devicesMounted', '6. ARE ALL DEVICES MOUNTED IN RIGHT LOCATIONS PER PANEL LAYOUT?')}
                            {renderCheckItem('visualChecks', 'backpanLabels', '7. ARE ALL BACKPAN LABELS ON AND APPLIED STRAIGHT?')}
                            {renderCheckItem('visualChecks', 'wireMarkersCorrect', '8. ARE ALL WIRE MARKERS ON NEAT AND ORIENTED CORRECTLY?')}
                            {renderCheckItem('visualChecks', 'doorFaceLabels', '9. HAS DOOR FACE LABELS/TAGS BEEN APPLIED?')}
                            {renderCheckItem('visualChecks', 'ulStickers', '10. HAS UL STICKERS BEEN APPLIED?')}
                            {renderCheckItem('visualChecks', 'terminalMarkersCorrect', '11. ARE ALL TERMINAL MARKERS PRESENT AND IN CORRECT ORDER?')}
                            {renderCheckItem('visualChecks', 'panduitCovers', '12. ARE PANDUIT COVERS CUT TO CORRECT LENGTH AND CLEAN?')}
                            {renderCheckItem('visualChecks', 'wiresTrunked', '13. HAVE ALL WIRES BEEN TRUNKED AND NEAT WITH ZIP TIES/SPIRAL WRAP?')}
                            {renderCheckItem('visualChecks', 'panelCleaned', '14. HAS PANEL BEEN CLEANED AND VACUUMED?')}
                            {renderCheckItem('visualChecks', 'doorsGrounded', '15. HAVE ALL DOORS BEEN GROUNDED?')}
                            {renderCheckItem('visualChecks', 'jumperBarsCut', '16. HAVE JUMPER BARS BEEN CUT CORRECTLY AND NEAT?')}
                            {renderCheckItem('visualChecks', 'ferrulesPresent', '17. ARE FERRULES PRESENT PER CUSTOMER NEEDS?')}
                            {renderCheckItem('visualChecks', 'overallWorkmanship', '18. DOES OVERALL PANEL SHOW GREAT WORKMANSHIP?')}
                        </AccordionDetails>
                    </Accordion>

                    {/* POINT TO POINT Checklist */}
                    <Accordion sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' },
                                backgroundColor: getSectionColor('pointToPointChecks') + '15'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
                                    Point to Point
                                </Typography>
                                <Chip
                                    label={`${getSectionProgress('pointToPointChecks').completed} / ${getSectionProgress('pointToPointChecks').total}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: getSectionColor('pointToPointChecks'),
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 24
                                    }}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {renderCheckItem('pointToPointChecks', 'wireAwgCorrect', '1. ALL WIRE AWG CORRECT PER SPEC FOR AMPS BEING APPLIED?')}
                            {renderCheckItem('pointToPointChecks', 'wireColorsCorrect', '2. ARE WIRE COLORS CORRECT PER CIRCUIT OR PER CUSTOMER?')}
                            {renderCheckItem('pointToPointChecks', 'volt600', '2A. 600 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt480', '2B. 480 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt380', '2C. 380 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt230', '2D. 230 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt208', '2E. 208 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt120', '2F. 120 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'volt24', '2G. 24 VOLT?')}
                            {renderCheckItem('pointToPointChecks', 'safetyCircuit', '2H. SAFETY CIRCUIT?')}
                            {renderCheckItem('pointToPointChecks', 'meterJumperBars', '3. USING METER DOES JUMPER BARS OHM ACROSS?')}
                            {renderCheckItem('pointToPointChecks', 'fusesInstalled', '4. ARE FUSES INSTALLED AND CORRECT PER DRAWING?')}
                            {renderCheckItem('pointToPointChecks', 'wiresTerminated', '5. HAVE ALL WIRES BEEN TERMINATED?')}
                            {renderCheckItem('pointToPointChecks', 'wiresTorqued', '6. HAVE ALL WIRES BEEN TERMINATED TO CORRECT TORQUE?')}
                            {renderCheckItem('pointToPointChecks', 'devicesTorqued', '7. HAVE ALL DEVICES, TERMINALS, AND JUMPER BARS BEEN TORQUE?')}
                            {renderCheckItem('pointToPointChecks', 'pulledTestApplied', '8. HAS PULLED TEST BEEN APPLIED?')}
                            {renderCheckItem('pointToPointChecks', 'panelWiredCorrectly', '9. WAS PANEL WIRED CORRECTLY PER DRAWINGS?')}
                        </AccordionDetails>
                    </Accordion>

                    {/* POWER CHECK */}
                    <Accordion sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' },
                                backgroundColor: getSectionColor('powerChecks') + '15'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
                                    Power Check
                                </Typography>
                                <Chip
                                    label={`${getSectionProgress('powerChecks').completed} / ${getSectionProgress('powerChecks').total}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: getSectionColor('powerChecks'),
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 24
                                    }}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {renderCheckItem('powerChecks', 'powerApplied', '1. HAS POWER BEEN APPLIED?')}
                            <Box sx={{
                                p: 2,
                                mb: 1,
                                borderRadius: 2,
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="1A. BY WHOM?"
                                    value={form.powerChecks.powerAppliedBy}
                                    onClick={() => {
                                        if (!submitted) setPowerAppliedByModalOpen(true);
                                    }}
                                    InputProps={{
                                        readOnly: true,
                                        style: { cursor: submitted ? 'default' : 'pointer' },
                                        endAdornment: !submitted && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setPowerAppliedByModalOpen(true)} size="small">
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted}
                                />
                            </Box>
                            {renderCheckItem('powerChecks', 'voltageVerified', '2. HAS CORRECT VOLTAGE BEEN VERIFIED BY METER?')}
                            {renderCheckItem('powerChecks', 'voltage600', '2A. 600V?')}
                            {renderCheckItem('powerChecks', 'voltage480', '2A. 480V?')}
                            {renderCheckItem('powerChecks', 'voltage230', '2B. 230V?')}
                            {renderCheckItem('powerChecks', 'voltage208', '2C. 208V?')}
                            {renderCheckItem('powerChecks', 'voltage120', '2D. 120V?')}
                            {renderCheckItem('powerChecks', 'componentsPowerUp', '3. DOES ALL COMPONENTS POWER UP?')}
                        </AccordionDetails>
                    </Accordion>

                    {/* FINAL */}
                    <Accordion sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' },
                                backgroundColor: getSectionColor('finalChecks') + '15'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
                                    Final Checklist
                                </Typography>
                                <Chip
                                    label={`${getSectionProgress('finalChecks').completed} / ${getSectionProgress('finalChecks').total}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: getSectionColor('finalChecks'),
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 24
                                    }}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {renderCheckItem('finalChecks', 'correctionCompleted', '1. HAS ALL CORRECTION BEEN COMPLETED?')}
                            {renderCheckItem('finalChecks', 'wireMarkersHeatShrunk', '2. HAS ALL WIRE MARKERS BEEN HEAT SRUNK?')}
                            {renderCheckItem('finalChecks', 'picturesTaken', '3. HAS PICTURES BEEN TAKEN?')}
                            {renderCheckItem('finalChecks', 'literaturePackaged', '4. HAS DEVICE LITERATURE BEEN PACKAGED TO SEND WITH PANEL?')}
                        </AccordionDetails>
                    </Accordion>

                    {/* Pictures */}
                    <Accordion sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' }
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1 }}>
                                    Pictures ({form.pictures?.length || 0})
                                </Typography>
                                {!submitted && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PhotoCameraIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCapturePhoto();
                                        }}
                                        sx={{ fontSize: '0.75rem', py: 0.5, mr: 1 }}
                                    >
                                        Add Photo
                                    </Button>
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {form.pictures && form.pictures.length > 0 ? (
                                <Grid container spacing={2}>
                                    {form.pictures.map((photo, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={photo.id}>
                                            <Card sx={{ 
                                                position: 'relative',
                                                boxShadow: 2,
                                                '&:hover': { boxShadow: 4 }
                                            }}>
                                                <Box sx={{ position: 'relative', paddingTop: '75%', overflow: 'hidden' }}>
                                                    <img
                                                        src={photo.dataUrl || photo.url}
                                                        alt={photo.name}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemovePhoto(photo.id)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(255, 255, 255, 1)'
                                                                }
                                                            }}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <CardContent sx={{ p: 1.5 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        multiline
                                                        rows={2}
                                                        placeholder="Add photo description..."
                                                        value={photo.description || ''}
                                                        onChange={(e) => handlePhotoDescriptionChange(photo.id, e.target.value)}
                                                        disabled={submitted}
                                                        InputProps={{
                                                            endAdornment: !submitted && (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setDictationField(`photo_${photo.id}`);
                                                                            startDictation({
                                                                                onResult: (transcript) => {
                                                                                    setForm(currentForm => {
                                                                                        const currentDesc = currentForm.pictures.find(p => p.id === photo.id)?.description || '';
                                                                                        const needsSpace = currentDesc && !currentDesc.endsWith(' ');
                                                                                        const newDesc = currentDesc + (needsSpace ? ' ' : '') + transcript + ' ';
                                                                                        const pictures = currentForm.pictures.map(p =>
                                                                                            p.id === photo.id ? { ...p, description: newDesc } : p
                                                                                        );
                                                                                        const updated = { ...currentForm, pictures };
                                                                                        queueSave(updated);
                                                                                        return updated;
                                                                                    });
                                                                                }
                                                                            });
                                                                        }}
                                                                        color={dictationField === `photo_${photo.id}` ? 'error' : 'default'}
                                                                    >
                                                                        {dictationField === `photo_${photo.id}` ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                fontSize: '0.875rem'
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        Photo {index + 1} • {new Date(photo.timestamp).toLocaleString()}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No pictures added yet. Click "Add Photo" to capture or upload pictures.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Signature */}
                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '40px !important',
                                '& .MuiAccordionSummary-content': { margin: '6px 0 !important' }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Supervisor Signature</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Supervisor *"
                                        value={form.supervisorName}
                                        onClick={() => {
                                            if (!submitted) setEmployeeModalOpen(true);
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={!!errors.supervisorName}
                                        helperText={errors.supervisorName}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Date"
                                        type="date"
                                        value={form.supervisorSignatureDate}
                                        onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                        onChange={(e) => handleFieldChange('supervisorSignatureDate', e.target.value)}
                                        disabled={submitted}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ style: { cursor: submitted ? 'default' : 'pointer' } }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        {form.supervisorSignature ? (
                                            <Box position="relative" display="inline-block">
                                                <img
                                                    src={form.supervisorSignature}
                                                    alt="Signature"
                                                    style={{
                                                        maxWidth: 200,
                                                        height: 60,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 4,
                                                        cursor: submitted ? 'default' : 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        if (!submitted) setSignatureModalOpen(true);
                                                    }}
                                                    title={submitted ? '' : 'Click to edit signature'}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearSignature}
                                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DrawIcon />}
                                                onClick={() => setSignatureModalOpen(true)}
                                                disabled={submitted}
                                            >
                                                Add Signature
                                            </Button>
                                        )}
                                    </Box>
                                    {errors.supervisorSignature && (
                                        <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                            {errors.supervisorSignature}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectEmployee}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SelectEmployeesAndTempsModal
                open={builderModalOpen}
                onClose={() => setBuilderModalOpen(false)}
                onSelect={handleSelectBuilder}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SelectEmployeesAndTempsModal
                open={fabricatorModalOpen}
                onClose={() => setFabricatorModalOpen(false)}
                onSelect={handleSelectFabricator}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SelectEmployeesAndTempsModal
                open={powerAppliedByModalOpen}
                onClose={() => setPowerAppliedByModalOpen(false)}
                onSelect={handleSelectPowerAppliedBy}
                singleSelect={true}
                alreadySelected={[]}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.supervisorName}
                onAccept={handleSignatureAccept}
                existingSignature={form.supervisorSignature}
            />

            <CameraCapture
                open={!!cameraTarget}
                onClose={() => setCameraTarget(null)}
                onCapture={handlePhotoCapture}
            />

            <Backdrop open={saving} sx={{ color: '#fff', zIndex: 9999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default PanelShopQcForm;

